import React, { useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { IoIosClose } from "react-icons/io";
import { IoLockClosedSharp } from "react-icons/io5";
import { Button, HelperText } from "@windmill/react-ui";
import LabelArea from "../../components/form/LabelArea";
import Logo from "../../components/logo/Logo";
import { AdminContext } from "../../context/AdminContext";
import useLoginSubmit from "../../hooks/formSubmits/useAuth";
import useGoogleAuth from "../../hooks/formSubmits/useGoogleAuth";
import MacbookImg from "../../assets/illustrations/data-analysis-1.png";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const history = useHistory();
  //
  const queryParams = new URLSearchParams(window.location.search);
  const emailFromUrl = queryParams.get('email') || '';

  const { onLoginSubmit, register, handleSubmit, errors, loading } =
    useLoginSubmit({emailFromUrl});
  const {
    state: { adminInfo },
  } = useContext(AdminContext);
  const { onSuccess, onFailure, GoogleLogin, clientId } = useGoogleAuth();

  if (adminInfo) {
    history.push("/");
  }

  return (
    <div className="min-h-screen bg-green-100">
      <div className="container mx-auto px-4 min-h-screen flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center py-6">
          <Logo margin="mt-0" className="w-40" />
          <a
            href={process.env.REACT_APP_SB_HOME_URL}
            className="bg-gray-100 hover:bg-gray-200 rounded-full h-8 w-8 flex justify-center items-center transition-colors duration-300"
          >
            <IoIosClose className="w-6 h-6 text-gray-600" />
          </a>
        </div>

        {/* Main Content */}
        <div className="flex-grow flex items-center justify-center">
          <div className="grid lg:grid-cols-2 gap-12 w-full max-w-6xl">
            {/* Login Form */}
            <div className="bg-green-50 rounded-xl p-8 shadow-sm border border-gray-200">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">
                Welcome Back
              </h1>
              <p className="text-gray-600 mb-8 text-center flex justify-center items-center gap-2">
                <IoLockClosedSharp className="text-green-600" />
                Secure Login
              </p>

              <form
                onSubmit={handleSubmit(onLoginSubmit)}
                className="space-y-6"
              >
                {/* Email Input */}
                <div>
                  <LabelArea label="Registered Email" />
                  <input
                    {...register("email", { 
                      required: true,
                      value: emailFromUrl 
                    })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-700 placeholder-gray-500 focus:outline-none focus:border-green-600 transition-colors duration-300"
                    type="email"
                    placeholder="john@example.com"
                  />
                  <HelperText valid={false}>
                    {errors.email?.type === "required" &&
                      "Please enter your registered email"}
                  </HelperText>
                </div>

                {/* Password */}
                <div className="relative">
                  <LabelArea label="Password" />
                  <div className="flex items-center relative">
                    <input
                      {...register("password", {
                        minLength: 4,
                        required: true,
                      })}
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-700 placeholder-gray-500 focus:outline-none focus:border-blue-600 transition-colors duration-300 pr-10"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <AiOutlineEye size={20} />
                        ) : (
                          <AiOutlineEyeInvisible size={20} />
                        )}
                      </button>
                    </div>
                  </div>
                  <HelperText valid={false}>
                    {errors.password?.type === "required" &&
                      "Password is required"}
                    {errors.password?.type === "minLength" &&
                      "Password must be at least 4 characters"}
                  </HelperText>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      {...register("remember_me")}
                      type="checkbox"
                      className="w-4 h-4 bg-white border-gray-300 rounded focus:ring-green-600 text-green-600"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      Remember me
                    </span>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-green-600 hover:text-green-700"
                  >
                    Forgot Password?
                  </Link>
                </div>

                {/* Submit Button */}
                <Button
                  disabled={loading}
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-300 disabled:opacity-50"
                >
                  {loading ? "Signing in..." : "Sign in"}
                </Button>

                {/* Divider */}
                <div className="relative flex items-center">
                  <div className="flex-grow border-t border-gray-200"></div>
                  <span className="flex-shrink-0 mx-4 text-gray-500">or</span>
                  <div className="flex-grow border-t border-gray-200"></div>
                </div>

                {/* Google Login */}
                <GoogleLogin
                  clientId={clientId}
                  onSuccess={onSuccess}
                  onFailure={onFailure}
                  cookiePolicy={"single_host_origin"}
                  isSignedIn={false}
                  prompt="select_account"
                  render={(renderProps) => (
                    <button
                      onClick={renderProps.onClick}
                      type="button"
                      className="hidden w-full bg-white hover:bg-gray-50 text-gray-700 font-medium py-3 px-4 rounded-lg border border-gray-300 transition-colors duration-300 flex items-center justify-center"
                    >
                      <FcGoogle className="w-5 h-5 mr-2" />
                      Continue with Google
                    </button>
                  )}
                />

                {/* Sign Up Link */}
                <p className="text-center text-gray-600">
                  Don't have an account?{" "}
                  <Link
                    to="/signup"
                    className="text-green-600 hover:text-green-700"
                  >
                    Sign up
                  </Link>
                </p>
              </form>
            </div>

            {/* Right Side Content */}
            <div className="hidden lg:flex flex-col justify-center">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Transform Your Data Into
                <span className="bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-transparent">
                  {" "}
                  Actionable Insights
                </span>
              </h2>
              <p className="text-xl text-gray-700 mb-8">
                Unlock the power of your data with AI, SQL, and drag-and-drop
                analytics. No coding skills required.
              </p>
              <img
                src={MacbookImg}
                alt="TalkingData.ai Dashboard"
                className="rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;